import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import $ from 'jquery';
import { connect } from 'react-redux';

import Master from '../Master';
import Paragraph from '../Paragraph';
import { FormatDate } from '../../utils/Functions';
import Button from '../Buttons';
import IziToast from '../IziToast';
import iziQuestion from '../IziToast/question';
import noImage from '../../img/no-image.png';
import UpdateTenant from './UpdateTenant';
import Collapsible from '../Panels/Collapsible';
import NextOfKin from '../NextOfKin';
import KinsTable from '../NextOfKin/KinsTable';
import PropertyAccess from '../../data/AccessRights';

// Reusable channel toggle for the CC Contact section
const ChannelToggle = ({
  label, icon, color, on, onChange,
}) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <span style={{ fontSize: 13, minWidth: 80 }}>
      <i className={`fa ${icon}`} style={{ color, marginRight: 6 }} />
      {label}
    </span>
    <div
      onClick={() => onChange(!on)}
      style={{
        cursor: 'pointer',
        width: 44,
        height: 24,
        borderRadius: 12,
        background: on ? '#c0392b' : '#ccc',
        position: 'relative',
        transition: 'background 0.2s',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 3,
          left: on ? 23 : 3,
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: '#fff',
          transition: 'left 0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,.3)',
        }}
      />
    </div>
  </div>
);

ChannelToggle.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  on: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

const TenantProfile = ({
  location: { property, tenant, userToken, source },
  user,
}) => {
  const history = useHistory();

  // get details from local storage
  if (typeof Storage !== 'undefined') {
    if (!tenant) {
      const tenantStr = window.localStorage.getItem('tenant');
      const propStr = window.localStorage.getItem('property');
      const userStr = window.localStorage.getItem('nyumbani');
      // eslint-disable-next-line no-param-reassign
      if (tenantStr) tenant = JSON.parse(tenantStr);
      // eslint-disable-next-line no-param-reassign
      if (propStr) property = JSON.parse(propStr);
      // eslint-disable-next-line no-param-reassign
      if (userStr) userToken = JSON.parse(userStr).token;
    }

    if (!source) {
      // eslint-disable-next-line no-param-reassign
      source = window.localStorage.getItem('source') || '/';
    }
  }

  const propertyName = (property && property.name) || '';

  const requestConfig = {
    headers: {
      Authorization: `Bearer ${userToken}`,
      'Content-Type': 'multipart/form-data',
    },
  };

  // state
  const [status, setStatus] = useState('');
  const [idOrPassport, setIdOrPassport] = useState('');
  const [phone, setPhone] = useState('');
  // Tenant's dedicated WhatsApp number. May be empty when unset — rendered
  // as "Not set" in the profile so it's obvious nothing has been configured.
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [currentFile, setCurrentFile] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [startDate, setStartDate] = useState(new Date());
  const [unitNumber, setUnitNumber] = useState('');
  const [nationality, setNationality] = useState('');
  const [nextOfKins, setNextOfKins] = useState([]);

  // CC contact — receives copies of invoices/receipts via enabled channels
  const [ccName, setCcName] = useState('');
  const [ccPhone, setCcPhone] = useState('');
  // Dedicated WhatsApp number for the CC contact. Separate from the SMS
  // phone above; left blank → CC WhatsApp is skipped even if the toggle is on.
  const [ccWhatsappPhone, setCcWhatsappPhone] = useState('');
  const [ccEmail, setCcEmail] = useState('');
  const [ccSmsEnabled, setCcSmsEnabled] = useState(false);
  const [ccWhatsappEnabled, setCcWhatsappEnabled] = useState(false);
  const [ccEmailEnabled, setCcEmailEnabled] = useState(false);
  const [ccSaving, setCcSaving] = useState(false);
  // Snapshot of what's CURRENTLY SAVED on the tenant (refreshed on mount /
  // after save / after clear). Used by the read-only summary so the user
  // can see "this is what's stored" — distinct from the editable form
  // state above which can change as they type.
  const [savedCcInfo, setSavedCcInfo] = useState(null);

  // copying state from props is anti-pattern but in our case we're not relying on props update
  useEffect(() => {
    if (tenant) {
      let currentUnitNumber = '';
      if (tenant.other_details) {
        const otherDetails = tenant.other_details;
        currentUnitNumber = otherDetails.unit_number;
      }

      setIdOrPassport(tenant.id_or_passport || '');
      setLastName(tenant.last_name || '');
      setEmail(tenant.email || '');
      setFirstName(tenant.first_name || '');
      setStartDate(new Date(+tenant.start_date) || new Date());
      setUnitNumber(currentUnitNumber || '');
      setStatus(tenant.status || '');
      setPhone(tenant.phone || '');
      setWhatsappPhone(tenant.whatsapp_phone || '');
      setNationality(tenant.nationality || '');
      if (tenant.images) {
        const { images } = tenant;
        if (images && images.length) {
          setCurrentFile(images[0].link);
        }
      }
      setNextOfKins(tenant.next_of_kin);
      // Load CC contact if configured
      const cc = tenant.other_details && tenant.other_details.cc_info;
      if (cc) {
        setSavedCcInfo(cc);
        setCcName(cc.name || '');
        setCcPhone(cc.phone || '');
        setCcWhatsappPhone(cc.whatsapp_phone || '');
        setCcEmail(cc.email || '');
        setCcSmsEnabled(!!cc.sms_enabled);
        setCcWhatsappEnabled(!!cc.whatsapp_enabled);
        setCcEmailEnabled(!!cc.email_enabled);
      } else {
        setSavedCcInfo(null);
      }
    }
  }, []);

  const reLoadTable = () => {
    const where = [{ id: tenant && tenant.id ? tenant.id : '' }];
    const url = `${process.env.REACT_APP_API_URL}tenants?where=${JSON.stringify(
      where,
    )}`;
    axios
      .get(url, requestConfig)
      .then((resp) => {
        if (resp) {
          const result = resp.data;
          if (result) {
            const updated = result.response;
            if (updated && updated.length) {
              const data = updated[0];
              if (data) {
                // eslint-disable-next-line no-param-reassign
                tenant = data;
                let currentUnitNumber = '';
                if (data.other_details) {
                  const otherDetails = data.other_details;
                  currentUnitNumber = otherDetails.unit_number;
                }
                setIdOrPassport(data.id_or_passport || '');
                setLastName(data.last_name || '');
                setEmail(data.email || '');
                setFirstName(data.first_name || '');
                setStartDate(new Date(+data.start_date) || new Date());
                setUnitNumber(currentUnitNumber || '');
                setStatus(data.status || '');
                setPhone(data.phone || '');
                setWhatsappPhone(data.whatsapp_phone || '');
                setNationality(data.nationality || '');
                if (data.images) {
                  const { images } = data;
                  if (images && images.length) {
                    setCurrentFile(images[0].link);
                  }
                }
                setNextOfKins(data.next_of_kin);
                // Reload CC contact
                const cc = data.other_details && data.other_details.cc_info;
                if (cc) {
                  setSavedCcInfo(cc);
                  setCcName(cc.name || '');
                  setCcPhone(cc.phone || '');
                  setCcWhatsappPhone(cc.whatsapp_phone || '');
                  setCcEmail(cc.email || '');
                  setCcSmsEnabled(!!cc.sms_enabled);
                  setCcWhatsappEnabled(!!cc.whatsapp_enabled);
                  setCcEmailEnabled(!!cc.email_enabled);
                } else {
                  setSavedCcInfo(null);
                  setCcName(''); setCcPhone(''); setCcWhatsappPhone(''); setCcEmail('');
                  setCcSmsEnabled(false); setCcWhatsappEnabled(false); setCcEmailEnabled(false);
                }
              }
            }
          }
        }
      })
      .catch((err) => {
        IziToast(err.message || 'An error occurred');
      });
  };

  const saveCCInfo = async () => {
    if (!tenant || !tenant.id) {
      IziToast('Tenant not loaded');
      return;
    }
    // Need a phone if SMS/WhatsApp on, need email if email on, need at least one channel
    const anyChannelOn = ccSmsEnabled || ccWhatsappEnabled || ccEmailEnabled;
    if (anyChannelOn) {
      if (ccSmsEnabled && !ccPhone.trim()) {
        IziToast('Phone number is required for SMS');
        return;
      }
      if (ccWhatsappEnabled && !ccWhatsappPhone.trim()) {
        IziToast('WhatsApp number is required for the WhatsApp channel');
        return;
      }
      if (ccEmailEnabled && !ccEmail.trim()) {
        IziToast('Email is required for the Email channel');
        return;
      }
    }
    setCcSaving(true);
    try {
      // Preserve any existing keys in other_details (unit_number etc.)
      const mergedOtherDetails = {
        ...(tenant.other_details || {}),
        cc_info: {
          name: ccName.trim(),
          phone: ccPhone.trim(),
          whatsapp_phone: ccWhatsappPhone.trim(),
          email: ccEmail.trim(),
          sms_enabled: ccSmsEnabled,
          whatsapp_enabled: ccWhatsappEnabled,
          email_enabled: ccEmailEnabled,
        },
      };
      const formData = new FormData();
      formData.append('id', tenant.id);
      formData.append('other_details', JSON.stringify(mergedOtherDetails));
      await axios.put(`${process.env.REACT_APP_API_URL}tenants`, formData, requestConfig);
      IziToast('CC contact saved');
      reLoadTable();
    } catch (err) {
      IziToast(err.response?.data?.error || err.message || 'Failed to save CC contact');
    }
    setCcSaving(false);
  };

  const clearCCInfo = async () => {
    if (!tenant || !tenant.id) return;
    iziQuestion('Remove the CC contact for this tenant?', async () => {
      setCcSaving(true);
      try {
        const other = { ...(tenant.other_details || {}) };
        delete other.cc_info;
        const formData = new FormData();
        formData.append('id', tenant.id);
        formData.append('other_details', JSON.stringify(other));
        await axios.put(`${process.env.REACT_APP_API_URL}tenants`, formData, requestConfig);
        IziToast('CC contact removed');
        setCcName(''); setCcPhone(''); setCcWhatsappPhone(''); setCcEmail('');
        setCcSmsEnabled(false); setCcWhatsappEnabled(false); setCcEmailEnabled(false);
        reLoadTable();
      } catch (err) {
        IziToast(err.response?.data?.error || err.message || 'Failed to remove CC contact');
      }
      setCcSaving(false);
    });
  };

  return (
    <Master
      title="Property Management"
      redirectLink="tenantprofile"
      source="Tenant Profile"
    >
      <UpdateTenant
        userToken={userToken}
        reLoadTable={reLoadTable}
        selectedTenant={tenant}
      />
      <div className="row">
        <div className="col-xs-3 bm-responsive-div">
          <img
            src={currentFile || noImage}
            alt="tenant"
            className="bordered_photo"
            style={{ width: '250px', height: '200px', margin: '10px' }}
          />

          {propertyName && (
            <Paragraph
              columnWidth={12}
              content={propertyName || ''}
              caption="Property Name"
            />
          )}
        </div>
        <div className="col-xs-9 bm-responsive-div">
          {tenant && (
            <>
              <div className="row">
                <Paragraph
                  columnWidth={3}
                  content={firstName}
                  caption="First Name"
                />
                <Paragraph
                  columnWidth={3}
                  content={lastName}
                  caption="Last Name"
                />
                <Paragraph columnWidth={3} content={phone} caption="Phone" />
                <Paragraph columnWidth={3} content={email} caption="Email" />
              </div>
              <div className="row">
                <Paragraph
                  columnWidth={3}
                  content={nationality}
                  caption="Nationality"
                />
                <Paragraph
                  columnWidth={3}
                  content={FormatDate(tenant.createdat)}
                  caption="Created On"
                />
                <Paragraph
                  columnWidth={3}
                  content={idOrPassport}
                  caption="ID/Passport"
                />
                <Paragraph
                  columnWidth={3}
                  content={FormatDate(tenant.lastupdated)}
                  caption="Updated On"
                />
              </div>
              <div className="row">
                <Paragraph
                  columnWidth={3}
                  content={unitNumber}
                  caption="Unit Number"
                />
                <Paragraph
                  columnWidth={3}
                  content={FormatDate(new Date(+startDate * 1000))}
                  caption="Start Date"
                />
                <Paragraph columnWidth={3} content={status} caption="Status" />
                <Paragraph
                  columnWidth={3}
                  content={whatsappPhone || 'Not set'}
                  caption="WhatsApp Number"
                />
              </div>
            </>
          )}
        </div>
        <div
          className="right"
          style={{ marginBottom: '20px', justifyContent: 'space-between' }}
        >
          <Button
            caption="<< Back To Tenant"
            cssClass="btn-success"
            onClick={() => {
              history.push({
                pathname: `/${source}`,
                property,
              });
              if (typeof Storage !== 'undefined') {
                window.localStorage.removeItem('property');
                window.localStorage.removeItem('tenant');
                window.localStorage.removeItem('source');
                window.localStorage.removeItem('unit');
              }
            }}
            type="button"
            isBlack
          />
          {tenant && user && PropertyAccess.includes(user.type) && (
            <>
              <Button
                caption="Edit Tenant"
                cssClass="btn-success"
                onClick={() => {
                  $('#tenantsModal').modal({
                    backdrop: 'static',
                    keyboard: false,
                  });
                }}
                type="button"
                isBlack
              />
            </>
          )}
        </div>
      </div>

      {tenant && (
        <div className="row">
          <Collapsible
            caption="Next Of Kin(s)"
            controlId="kins"
            columnWidth={12}
          >
            {user && PropertyAccess.includes(user.type) && (
              <NextOfKin
                tenantId={tenant.id}
                nextOfKins={nextOfKins}
                userToken={userToken}
                reLoadTable={reLoadTable}
              />
            )}
            <div className="row">
              {tenant && (
                <KinsTable
                  tenantId={tenant.id}
                  reLoadTable={reLoadTable}
                  userToken={userToken}
                  nextOfKins={nextOfKins}
                />
              )}
            </div>
          </Collapsible>
        </div>
      )}

      {tenant && user && PropertyAccess.includes(user.type) && (
        <div className="row">
          <Collapsible
            caption="CC Contact (copy of invoices / receipts)"
            controlId="ccinfo"
            columnWidth={12}
          >
            <div className="row" style={{ padding: '0 15px' }}>
              <div className="col-md-12" style={{ marginBottom: 14 }}>
                <small style={{ color: '#777', fontStyle: 'italic' }}>
                  Optional. Send a copy of every invoice and receipt for this tenant
                  to another person (e.g. a guarantor, family member, or accountant).
                  Toggle which channels they should receive.
                </small>
              </div>

              {/* ── Read-only "currently saved" snapshot ──────────────── */}
              {/* So the user can immediately see what's stored before deciding to edit. */}
              <div className="col-md-12" style={{ marginBottom: 14 }}>
                {savedCcInfo ? (
                  <div
                    style={{
                      background: '#f1f8f3',
                      border: '1px solid #cfe5d4',
                      borderRadius: 6,
                      padding: '12px 14px',
                      fontSize: 13,
                      color: '#2c3e50',
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>
                      <i
                        className="fa fa-check-circle"
                        style={{ marginRight: 6, color: '#27ae60' }}
                      />
                      Currently saved CC contact
                    </div>
                    <div style={{ lineHeight: '1.7' }}>
                      <span style={{ color: '#7c8896' }}>Name:</span>{' '}
                      <strong>{savedCcInfo.name || <span style={{ color: '#bbb', fontWeight: 'normal' }}>—</span>}</strong>
                      <span style={{ marginLeft: 14, color: '#7c8896' }}>Phone:</span>{' '}
                      <strong>{savedCcInfo.phone || <span style={{ color: '#bbb', fontWeight: 'normal' }}>—</span>}</strong>
                      <span style={{ marginLeft: 14, color: '#7c8896' }}>WhatsApp:</span>{' '}
                      <strong>{savedCcInfo.whatsapp_phone || <span style={{ color: '#bbb', fontWeight: 'normal' }}>—</span>}</strong>
                      <span style={{ marginLeft: 14, color: '#7c8896' }}>Email:</span>{' '}
                      <strong>{savedCcInfo.email || <span style={{ color: '#bbb', fontWeight: 'normal' }}>—</span>}</strong>
                      <br />
                      <span style={{ color: '#7c8896' }}>Active channels:</span>{' '}
                      {[
                        savedCcInfo.sms_enabled && 'SMS',
                        savedCcInfo.whatsapp_enabled && 'WhatsApp',
                        savedCcInfo.email_enabled && 'Email',
                      ].filter(Boolean).length > 0 ? (
                        [
                          savedCcInfo.sms_enabled && 'SMS',
                          savedCcInfo.whatsapp_enabled && 'WhatsApp',
                          savedCcInfo.email_enabled && 'Email',
                        ].filter(Boolean).map((c) => (
                          <span
                            key={c}
                            style={{
                              display: 'inline-block',
                              marginLeft: 6,
                              padding: '0.2em 0.55em',
                              borderRadius: 10,
                              background: '#e8f6ee',
                              color: '#2e7d4f',
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            {c}
                          </span>
                        ))
                      ) : (
                        <span style={{ color: '#b9831e' }}>none enabled</span>
                      )}
                    </div>
                    <div style={{ marginTop: 8, fontSize: 12, color: '#7c8896' }}>
                      Edit below and click <strong>Save CC Contact</strong> to update.
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      background: '#fff8e1',
                      border: '1px solid #ffe082',
                      borderRadius: 6,
                      padding: '10px 14px',
                      fontSize: 13,
                      color: '#7a5d00',
                    }}
                  >
                    <i className="fa fa-info-circle" style={{ marginRight: 6, color: '#f39c12' }} />
                    No CC contact configured for this tenant yet. Fill in the
                    fields below and click <strong>Save CC Contact</strong>.
                  </div>
                )}
              </div>

              <div className="col-md-4" style={{ marginBottom: 12 }}>
                <label className="control-label">Name</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter contact name"
                  value={ccName}
                  onChange={(e) => setCcName(e.target.value)}
                />
              </div>
              <div className="col-md-4" style={{ marginBottom: 12 }}>
                <label className="control-label">Phone Number</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. +254712345678"
                  value={ccPhone}
                  onChange={(e) => setCcPhone(e.target.value)}
                />
              </div>
              <div className="col-md-4" style={{ marginBottom: 12 }}>
                <label className="control-label">Email</label>
                <input
                  type="email"
                  className="form-control"
                  placeholder="e.g. cc@example.com"
                  value={ccEmail}
                  onChange={(e) => setCcEmail(e.target.value)}
                />
              </div>
              <div className="col-md-4" style={{ marginBottom: 12 }}>
                <label className="control-label">WhatsApp Number (optional)</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. +254712345678 (blank → skip)"
                  value={ccWhatsappPhone}
                  onChange={(e) => setCcWhatsappPhone(e.target.value)}
                />
              </div>

              <div className="col-md-12" style={{ marginTop: 8 }}>
                <label className="control-label" style={{ display: 'block', marginBottom: 8 }}>
                  Channels
                </label>
                <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
                  <ChannelToggle
                    label="SMS"
                    icon="fa-comment-o"
                    color="#34495e"
                    on={ccSmsEnabled}
                    onChange={setCcSmsEnabled}
                  />
                  <ChannelToggle
                    label="WhatsApp"
                    icon="fa-whatsapp"
                    color="#25D366"
                    on={ccWhatsappEnabled}
                    onChange={setCcWhatsappEnabled}
                  />
                  <ChannelToggle
                    label="Email"
                    icon="fa-envelope"
                    color="#3498db"
                    on={ccEmailEnabled}
                    onChange={setCcEmailEnabled}
                  />
                </div>
              </div>

              <div className="col-md-12" style={{ marginTop: 18, textAlign: 'right' }}>
                {(ccName || ccPhone || ccEmail) && (
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={clearCCInfo}
                    style={{ marginRight: 8 }}
                    disabled={ccSaving}
                  >
                    Remove CC Contact
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={saveCCInfo}
                  disabled={ccSaving}
                >
                  {ccSaving ? 'Saving...' : 'Save CC Contact'}
                </button>
              </div>
            </div>
          </Collapsible>
        </div>
      )}
    </Master>
  );
};

TenantProfile.propTypes = {
  userToken: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  location: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  user: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
};

TenantProfile.defaultProps = {
  userToken: {},
  location: {},
};

const mapStateToProps = (state) => {
  return state;
};

export default connect(mapStateToProps)(TenantProfile);
